import type { ICandidate } from "@bbj-types/candidate";
import type { ISupportCourse } from "@bbj-types/supportCourse";
import type { IAuthor, IUser } from "@bbj-types/user";
import type { ObjectId, Override } from "@bbj-types/utils";
import type { IWorkshop, IWorkshopParticipant } from "@bbj-types/workshop";
import { useSuspense } from "@root/api/tools/axios";
import { workshopsAxiosInstance } from "./axios";

export type TLastCommunicationIcon = "AccessTimeIcon" | "DoneIcon" | "DoneAllIcon" | "ErrorOutlineIcon";

export type TWorkshopParticipants = (Override<Omit<IWorkshopParticipant, "_id">, { candidate: ObjectId }> &
  Pick<
    ICandidate,
    | "firstName"
    | "lastName"
    | "address"
    | "coachingStep"
    | "optInPhone"
    | "optInEmail"
    | "mail"
    | "phone"
    | "profileId"
    | "birthDate"
    | "team"
    | "lastEmailStatus"
    | "isEmailValid"
  > & {
    lastCommunicationIcon?: TLastCommunicationIcon;
    authors: IAuthor[];
  })[];

export type TWorkshopParticipantsWithSelection = (TWorkshopParticipants[0] & { isSelected?: boolean })[];

export type TWorkshopParticipantPresence = {
  isPresent: boolean;
  justifiedAbsence?: boolean;
  presenceTime?: number;
};

export type TGetWorkshop = Override<
  Pick<
    IWorkshop,
    | "_id"
    | "organisation"
    | "sessionType"
    | "organizer"
    | "owner"
    | "mainAnimator"
    | "otherAnimators"
    | "address"
    | "date"
    | "startTime"
    | "endTime"
    | "convocationTimes"
    | "description"
    | "documents"
    | "status"
    | "isDone"
    | "isCancelled"
    | "supportCourse"
    | "createdAt"
    | "updatedAt"
    | "scheduledAppointmentReminder"
  >,
  {
    participants: TWorkshopParticipants;
    mainAnimator?: Pick<IUser, "_id" | "firstName" | "lastName" | "isDisabled">;
    otherAnimators: Pick<IUser, "_id" | "firstName" | "lastName" | "isDisabled">[];
    supportCourse?: Pick<ISupportCourse, "_id" | "name" | "prfe2IdCourse">;
  }
>;

export const useSuspenseGetWorkshop = (id: ObjectId) => {
  return useSuspense(
    () =>
      workshopsAxiosInstance.get<TGetWorkshop>(`/${id}`, {
        withCredentials: true,
      }),
    [id]
  );
};
